import { useMemo, useState } from 'react';
import { Button, Box, Typography, Paper, Stack, Alert, Fade, CircularProgress, Avatar, TextField, Chip } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import { sendOpenAiRequest, OpenAIResponse } from '@/services';
import { getErrorMessage } from '@/utils/errors';
import type { AxiosError } from 'axios';

import type { ParsedTranscript, CombinedTranscript } from '@/types/transcript';

interface LeadGptProps {
    parsedTranscript?: ParsedTranscript | null;
}

interface LeadGptState {
    loading: boolean;
    result: OpenAIResponse | null;
    error: string | null;
    question: string;
    requestType: 'summary' | 'question';
}

const LeadGpt: React.FC<LeadGptProps> = ({ parsedTranscript }) => {
    const [state, setState] = useState<LeadGptState>({
        loading: false,
        result: null,
        error: null,
        question: '',
        requestType: 'question',
    });

    const exampleQuestions = [
        { text: 'Give me a summary of the call', type: 'summary' as const },
        { text: "What was the customer's main concern?", type: 'question' as const },
        { text: 'How was agent performance in this call?', type: 'question' as const },
    ];

    const transcript = parsedTranscript?.transcript ?? null;

    const handleQueryClick = async () => {
        setState((prev) => ({ ...prev, loading: true, error: null, result: null }));

        try {
            if (!transcript) {
                setState((prev) => ({ ...prev, error: 'No transcript provided', loading: false }));
                return;
            }

            const requestType = state.requestType;
            const question = state.question;

            const response = await sendOpenAiRequest({
                transcript: transcript as CombinedTranscript[],
                question: requestType === 'question' ? question : undefined,
                requestType,
            });

            setState((prev) => ({
                ...prev,
                result: response,
                loading: false,
                requestType: requestType === 'summary' ? 'question' : requestType,
            }));
        } catch (err) {
            const message = getErrorMessage(err as AxiosError) ?? 'Error getting response from AI';
            setState((prev) => ({
                ...prev,
                error: message,
                loading: false,
                requestType: prev.requestType === 'summary' ? 'question' : prev.requestType,
            }));
        }
    };

    const handleQuestionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prev) => ({
            ...prev,
            question: event.target.value,
            requestType: 'question',
        }));
    };

    const handleExampleClick = (text: string, type: 'summary' | 'question') => {
        setState((prev) => ({
            ...prev,
            question: type === 'question' ? text : '',
            requestType: type,
            result: null,
        }));
    };

    const queryButtonDisabled = state.loading || !transcript || (state.requestType === 'question' && !state.question);
    const queryButtonStartIcon = state.loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome sx={{ fontSize: 10 }} />;

    const queryButtonLabel = useMemo(() => {
        if (state.loading) return 'Requesting...';
        if (!transcript) return 'No transcript available';
        if (state.requestType === 'summary') return 'Get summary';

        return 'Ask question';
    }, [state.loading, transcript, state.requestType]);

    return (
        <Paper sx={{ p: 2 }}>
            <Stack direction="column" spacing={1}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, mb: 2 }}>
                    <Box sx={{ flex: '1 1 60%' }}>
                        <Stack direction="column" spacing={1}>
                            <Typography component="label" htmlFor="question-input">
                                Ask a question about the call
                            </Typography>
                            <TextField
                                id="question-input"
                                variant="outlined"
                                fullWidth
                                value={state.question}
                                onChange={handleQuestionChange}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !queryButtonDisabled) {
                                        handleQueryClick();
                                    }
                                }}
                                placeholder="E.g. what was the customer's main concern?"
                                disabled={state.loading || state.requestType === 'summary'}
                            />
                            <Box sx={{ mt: 1 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleQueryClick}
                                    disabled={queryButtonDisabled}
                                    startIcon={queryButtonStartIcon}
                                >
                                    {queryButtonLabel}
                                </Button>
                            </Box>
                        </Stack>
                    </Box>
                    <Box sx={{ flex: '1 1 40%', display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Try asking:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                            {exampleQuestions.map((example, index) => (
                                <Chip
                                    key={index}
                                    label={example.text}
                                    size="small"
                                    onClick={() => handleExampleClick(example.text, example.type)}
                                    color={
                                        state.requestType === example.type &&
                                        (example.type === 'summary' || state.question === example.text)
                                            ? 'primary'
                                            : 'default'
                                    }
                                    variant={
                                        state.requestType === example.type &&
                                        (example.type === 'summary' || state.question === example.text)
                                            ? 'filled'
                                            : 'outlined'
                                    }
                                    sx={{ justifyContent: 'flex-start' }}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Stack>
            {state.error && (
                <Fade in={!!state.error} timeout={500}>
                    <Box sx={{ mt: 2 }}>
                        <Alert severity="error">
                            <Typography variant="body2" color="error">
                                {state.error}
                            </Typography>
                        </Alert>
                    </Box>
                </Fade>
            )}
            {state.result && (
                <Fade in={!!state.result} timeout={500}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            backgroundColor: 'secondary.light',
                            p: 1,
                            gap: 1,
                            mt: 2,
                            borderRadius: 1,
                        }}
                    >
                        <Box>
                            <Avatar
                                sx={{
                                    bgcolor: 'primary.dark',
                                    color: 'white',
                                    width: 32,
                                    height: 32,
                                    fontSize: '0.75rem',
                                    flexShrink: 0,
                                    borderRadius: 1.5,
                                }}
                            >
                                <AutoAwesome fontSize="small" color="inherit" />
                            </Avatar>
                        </Box>
                        <Stack direction="column" spacing={0.25} mt={0.2}>
                            {'summary' in state.result && (
                                <>
                                    <Box>
                                        <Typography variant="body1" gutterBottom>
                                            {state.result.summary}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            Call outcome:
                                            <Typography component="span" fontWeight="bold">
                                                {state.result.outcome}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            Sentiment tone:
                                            <Typography component="span" fontWeight="bold">
                                                {state.result.tone}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </>
                            )}
                            {'answer' in state.result && (
                                <Box>
                                    <Typography variant="body1">{state.result.answer}</Typography>
                                </Box>
                            )}
                        </Stack>
                    </Box>
                </Fade>
            )}
        </Paper>
    );
};

export default LeadGpt;
